import React from 'react';
import styled from 'astroturf/react';

import { isDownloadRead } from '../openerIntent';
import type { ArmSpec } from '../armRegistry';

type NodeState = 'done' | 'active' | 'target';

type StepNode = { label: string; state: NodeState };

const NODES: ReadonlyArray<StepNode> = [
  { label: 'Your paper', state: 'done' },
  { label: 'Create free account', state: 'active' },
  { label: 'Download PDF', state: 'target' },
];

const BLACK = 'var(--brand-colors-neutral-black, #141413)';
const BLACK_FADED = 'rgba(20, 20, 19, 0.25)';

const Stepper = styled.ol`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  list-style: none;
  margin: 0 0 var(--spacing-sm-24, 24px);
  padding: 24px 0 0;
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
`;

const NodeItem = styled.li`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: 0;
  animation: step-node-in 240ms ease var(--node-delay, 0ms) both;

  &::before {
    content: '';
    position: absolute;
    top: 9px;
    right: 50%;
    left: -50%;
    height: 2px;
    background: var(--link-color, rgba(20, 20, 19, 0.25));
    z-index: 0;
  }
  &:first-child::before {
    display: none;
  }

  @keyframes step-node-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`;

const Marker = styled.span`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  line-height: 1;
  animation: step-marker-pop 260ms cubic-bezier(0.2, 0.8, 0.3, 1.4)
    var(--node-delay, 0ms) both;

  @keyframes step-marker-pop {
    from {
      transform: scale(0.6);
    }
    to {
      transform: scale(1);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
`;

const Check = styled.span`
  animation: step-check-pop 220ms cubic-bezier(0.2, 0.8, 0.3, 1.4) 120ms both;

  @keyframes step-check-pop {
    from {
      opacity: 0;
      transform: scale(0);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const NodeLabel = styled.span`
  font-size: 12px;
  line-height: 1.3;
  text-align: center;
`;

const Cue = styled.span`
  position: absolute;
  bottom: 100%;
  left: 50%;
  margin-bottom: 4px;
  font-size: 18px;
  line-height: 1;
  color: var(--brand-colors-neutral-black, #141413);
  transform: translateX(-50%);
  animation: step-cue-bounce 1.4s ease-in-out infinite;

  @keyframes step-cue-bounce {
    0%,
    100% {
      transform: translateX(-50%) translateY(0);
    }
    50% {
      transform: translateX(-50%) translateY(3px);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

function markerStyle(state: NodeState): React.CSSProperties {
  if (state === 'done') {
    return { background: BLACK, borderColor: BLACK, color: '#fff' };
  }
  if (state === 'active') {
    return {
      background: '#fff',
      borderColor: BLACK,
      color: BLACK,
      boxShadow: `0 0 0 4px ${BLACK_FADED}`,
    };
  }
  return { background: '#fff', borderColor: BLACK_FADED, color: BLACK };
}

function labelStyle(state: NodeState): React.CSSProperties {
  return { color: BLACK, fontWeight: state === 'active' ? 600 : 500 };
}

const StepTwoCheckpoint: React.FC = () => (
  <Stepper role="list" aria-label="Sign-up progress">
    {NODES.map((node, index) => {
      const linkFilled = index > 0 && NODES[index - 1].state === 'done';
      return (
        <NodeItem
          key={node.label}
          role="listitem"
          aria-current={node.state === 'active' ? 'step' : undefined}
          aria-label={
            node.state === 'done' ? `${node.label} (completed)` : undefined
          }
          style={
            {
              '--node-delay': `${index * 70}ms`,
              '--link-color': linkFilled ? BLACK : BLACK_FADED,
            } as React.CSSProperties
          }
        >
          {node.state === 'active' && <Cue aria-hidden="true">▼</Cue>}
          <Marker style={markerStyle(node.state)}>
            {node.state === 'done' && <Check aria-hidden="true">✓</Check>}
            {node.state === 'active' && (
              <Dot aria-hidden="true" style={{ background: BLACK }} />
            )}
          </Marker>
          <NodeLabel style={labelStyle(node.state)}>{node.label}</NodeLabel>
        </NodeItem>
      );
    })}
  </Stepper>
);

export const stepTwoSpec: ArmSpec = {
  slots: (ctx) => {
    if (!isDownloadRead(ctx.detail)) return undefined;
    return { aboveHeader: <StepTwoCheckpoint /> };
  },
  stamp: (ctx) => ({
    render_state: isDownloadRead(ctx.detail) ? 'rendered' : 'fallback',
  }),
};
